import { CommonModule } from "@angular/common";
import { Component, inject, model } from "@angular/core";
import { AuthService } from "../../../services/api/auth/auth.service";
import { Router } from "@angular/router";
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from "@angular/forms";
import { interval, Subscription, take } from "rxjs";
import { SnackbarService } from "../../../services/snackbar/snackbar.service";
import { ThemeService } from "../../../services/theme/theme.service";

@Component({
  selector: "app-login",
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.scss",
})
export class LoginComponent {
  loginForm: FormGroup;
  verificationForm: FormGroup;
  errorMessage: string = "";
  showPassword = false;
  // middle state and email
  verificationMode = false;
  verificationEmail = "";
  isResendDisabled = false;
  countdown = 0;
  private countdownSub?: Subscription;
  private snackService = inject(SnackbarService);
  themeService = inject(ThemeService);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
  ) {
    this.loginForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
    });

    this.verificationForm = this.fb.nonNullable.group({
      email: this.fb.control<string>(this.verificationEmail, {
        validators: [Validators.required, Validators.email],
      }),
      verification_code: [
        "",
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
      trust_device: [false, Validators.required],
    });
  }

  private readonly roleRoutes: Record<string, string> = {
    GROWER: "/field",
    ENTITY: "/entity/map-view",
    MANAGER: "/manager/fields-view",
    ADMIN: "/admin",
  };

  private redirectByRole(): void {
    const role = this.authService.getRole();
    const route = this.roleRoutes[role];
    if (route) {
      this.router.navigate([route]);
    }
  }

  getVerificationCodeErrorMessage() {
    const control = this.verificationForm.get("verification_code");
    if (!control) return null;
    if (control.invalid && (control.dirty || control.touched)) {
      if (control.hasError("required")) {
        return "This field is required";
      }
      if (control.hasError("minlength") || control.hasError("maxlength")) {
        return "Verification code must be exactly 6 characters long";
      }
    }
    return null;
  }

  verifyCode() {
    const { verification_code, trust_device } = this.verificationForm.value;
    const signInVerificationCodeDto = {
      email: this.verificationEmail,
      verification_code: verification_code?.trim(),
      trust_device: trust_device,
    };
    this.authService.verifySigninCode(signInVerificationCodeDto).subscribe({
      next: (response) => {
        if (response && response.state === "signin") {
          // Should be replaced by Notification Modal / Popup.
          this.errorMessage = "";
          this.snackService.show({
            message: "Verified successfully. Logging in...",
            duration: 5000,
          });
          this.redirectByRole();
        }
      },
      error: (err) => {
        this.errorMessage =
          err.message || "Verification failed. Please try again.";
        console.error("Verified error", err);
      },
    });
  }

  resendCode() {
    // Call your resend API here
    // Disable button
    this.isResendDisabled = true;
    this.countdown = 60; // 60 seconds
    // Start countdown timer
    this.countdownSub = interval(1000)
      .pipe(take(60))
      .subscribe({
        next: () => {
          this.countdown--;
        },
        complete: () => {
          this.isResendDisabled = false;
        },
      });

    this.authService.resendCodeSignin(this.verificationEmail).subscribe({
      next: (response) => {
        if (response && response.state === "signin_code_resent") {
          this.errorMessage = "";
          // Should be replaced by Notification Modal / Popup.
          this.snackService.show({
            message: "Verification code resent successfully.",
            duration: 5000,
          });
        }
      },
      error: (err) => {
        this.errorMessage =
          err.message || "Resend code failed. Please try again.";
      },
    });
  }

  getInputErrorMessage(controlName: string) {
    const control = this.loginForm.get(controlName);
    if (!control) return null;
    if (control.invalid && (control.dirty || control.touched)) {
      if (control.hasError("required")) {
        return "This field is required";
      }
      if (control.hasError("email")) {
        return "Not a valid email";
      }
    }
    return null;
  }

  login(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    const { email, password } = this.loginForm.value;
    this.authService.login(email, password).subscribe({
      next: (response) => {
        // check if the state if signin_verify, update the verificationMode to true
        this.errorMessage = "";
        if (
          response &&
          response.email &&
          response.state === "signin_verify_state"
        ) {
          this.verificationMode = true;
          this.verificationEmail = response.email;
          return;
        }

        if (response && response.state === "signin") {
          // Should be replaced by Notification Modal / Popup.
          this.snackService.show({
            message: "Login successfully",
            duration: 3000,
          });
          this.redirectByRole();
        }
      },
      error: (err) => {
        this.errorMessage = err.message || "Sign in failed. Please try again.";
        console.error("Login error", err);
      },
    });
  }

  // Could direct to signup page and forgot password page.
  directTo(route: string) {
    this.router.navigate([route]);
  }

  goBackToLogin() {
    // Reset form state
    this.verificationForm.reset();
    // Switch back to signup view
    this.verificationMode = false;
    // Optionally also stop countdown if running
    this.countdownSub?.unsubscribe();
    this.isResendDisabled = false;
    this.errorMessage = "";
  }
}
